import Knock from "@knocklabs/client";
import { environmentConfig } from "@src/appV2/environment";
import { knock } from "@src/appV2/environment/config";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

interface RegisterPushTokenWithKnockParams {
  token: string;
  userId: string;
  knockToken: string;
}

export async function registerPushTokenInKnock(
  params: RegisterPushTokenWithKnockParams
): Promise<void> {
  const { token, userId, knockToken } = params;
  const channelId = Platform.isIOS ? knock.channels.workplaceApns : knock.channels.workplaceFcm;
  const knockClient = getKnockClient();

  try {
    knockClient.authenticate({ id: userId }, knockToken);

    const existingTokens = await getExistingTokens(knockClient, channelId);

    if (existingTokens.includes(token)) {
      return;
    }

    await knockClient.user.setChannelData({
      channelId,
      channelData: { tokens: [...existingTokens, token] },
    });

    logEvent(APP_V2_APP_EVENTS.KNOCK_PUSH_TOKEN_REGISTERED, { userId });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.KNOCK_PUSH_TOKEN_REGISTRATION_FAILED, {
      error,
      metadata: { userId },
    });
  } finally {
    knockClient.teardown();
  }
}

interface UnregisterPushTokenFromKnockParams {
  token: string;
  userId: string;
  knockToken: string;
}

export async function unregisterPushTokenInKnock(
  params: UnregisterPushTokenFromKnockParams
): Promise<void> {
  const { token, userId, knockToken } = params;
  const channelId = Platform.isIOS ? knock.channels.workplaceApns : knock.channels.workplaceFcm;
  const knockClient = getKnockClient();

  try {
    knockClient.authenticate({ id: userId }, knockToken);

    const existingTokens = await getExistingTokens(knockClient, channelId);
    const updatedTokens = existingTokens.filter((existingToken) => existingToken !== token);

    await knockClient.user.setChannelData({
      channelId,
      channelData: { tokens: updatedTokens },
    });

    logEvent(APP_V2_APP_EVENTS.KNOCK_PUSH_TOKEN_UNREGISTERED, { userId });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.KNOCK_PUSH_TOKEN_UNREGISTRATION_FAILED, {
      error,
      metadata: { userId },
    });
  } finally {
    knockClient.teardown();
  }
}

export type KnockMessageEngagementStatus = "seen" | "interacted" | "read" | "archived";

interface UpdateKnockMessageStatusParams {
  knockMessageId: string;
  status: KnockMessageEngagementStatus;
  userId: string;
  knockToken: string;
}

export async function updateKnockMessageStatus(
  params: UpdateKnockMessageStatusParams
): Promise<void> {
  const { knockMessageId, status, userId, knockToken } = params;
  const knockClient = getKnockClient();

  try {
    knockClient.authenticate({ id: userId }, knockToken);
    await knockClient.messages.updateStatus(knockMessageId, status);
    logEvent(APP_V2_APP_EVENTS.KNOCK_MESSAGE_STATUS_UPDATED, { userId });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.KNOCK_MESSAGE_STATUS_UPDATE_FAILED, {
      error,
      metadata: { knockMessageId, status, userId },
    });
  } finally {
    knockClient.teardown();
  }
}

async function getExistingTokens(knockClient: Knock, channelId: string): Promise<string[]> {
  try {
    const channelData = await knockClient.user.getChannelData<{ tokens: string[] }>({
      channelId,
    });
    return channelData.data.tokens ?? [];
  } catch {
    return [];
  }
}

function getKnockClient(): Knock {
  // The client uses 3 retries internally with exponential backoff.
  return new Knock(environmentConfig.REACT_APP_KNOCK_PUBLIC_API_KEY);
}
